import { Injectable, NgZone } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { environment } from '../../../environments/environment';
import { Subject } from 'rxjs';
import { UserService } from './user.service';

export interface OrgEventEnvelope {
  eventName: string;
  payloadJson: string;
}

@Injectable({ providedIn: 'root' })
export class RealtimeService {
  private hub?: signalR.HubConnection;

  private orgEventsSubject = new Subject<OrgEventEnvelope>();
  orgEvents$ = this.orgEventsSubject.asObservable();

  constructor(private userService: UserService, private zone: NgZone) {
    // Wake / tab focus / network back
    window.addEventListener('online', () => this.zone.run(() => this.ensureConnected()));
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) this.zone.run(() => this.ensureConnected());
    });

    // Most reliable wake detector: sleep gap
    let last = Date.now();
    setInterval(() => {
      const now = Date.now();
      if (now - last > 30_000) { // woke up after sleep (gap > 30s)
        this.zone.run(() => this.ensureConnected(true));
      }
      last = now;
    }, 5_000);
  }

  start() {
    if (this.hub && this.hub.state !== signalR.HubConnectionState.Disconnected) 
      return;

    const token = this.userService.accessToken();
    if (!token) return;

    this.hub = new signalR.HubConnectionBuilder()
      .withUrl(`${environment.apiBaseUrl}/hubs/app`, {
        accessTokenFactory: () => {
            return this.userService.accessToken()!;
        },
        withCredentials: true
      })
      .withAutomaticReconnect([0, 2000, 10000, 30000])
      .build();

    this.hub.on('OrgEvent', (evt: OrgEventEnvelope) => {
      this.zone.run(() => this.orgEventsSubject.next(evt));
    });

    this.hub.on('ForceLogout', (_msg: any) => {
        this.zone.run(() => this.userService.remove());
    });

    this.hub.start().catch(err => console.error('SignalR start error', err));

    if(environment.logSignalRClosures){
        this.hub.onreconnecting(err => console.warn('reconnecting...', err));
        this.hub.onreconnected(id => console.warn('reconnected', id));
        this.hub.onclose(err => console.error('hub closed:', err));
    }
  }

  stop() {
    this.hub?.stop();
    this.hub = undefined;
  }

  refreshConnection(){    
    if (this.hub?.state === signalR.HubConnectionState.Connected)
    {
      this.stop();
      this.start();
    }
  }

  private ensureConnected(forceRebuild = false) {
    if (!this.userService.accessToken()) return;

    if (!this.hub) {
      this.start();
      return;
    }

    if (forceRebuild) {
      // After sleep, safest is full rebuild
      this.stop();
      this.start();
      return;
    }

    if (this.hub.state === signalR.HubConnectionState.Disconnected) {
      this.hub.start().catch(() => {
        // If start fails (stale transport), rebuild
        this.stop();
        this.start();
      });
    }
  }
}
